import { useKnockFeed } from '@knocklabs/react-notification-feed'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useEffect } from 'react'
import ReactFocusLock from 'react-focus-lock'
import { IoCloseSharp } from 'react-icons/io5'
import { useKeyPressEvent, useMedia } from 'react-use'
import styled from 'styled-components'
import { AchievementType } from 'types/graphql'
import { media, phoneMaxWidth } from 'utils/media'
import { LinkButton } from 'lib/Button/Button'
import { usePopoverController } from 'lib/Popover'
import { MaxWidthContainer } from 'lib/Section'
import { ShowForMobile } from 'lib/ShowComponent'
import { useCurrentUser } from 'modules/auth/useCurrentUser'
import { useMyBrands } from 'modules/brandmin/list/queries/useMyContributableBrands'
import { BrandPreviewLink } from 'modules/brands/common/BrandPreviewLink'
import { UserPreviewLink } from 'modules/community/UserPreviewLink'
import { useUserAchievements } from 'modules/user/queries/useUserAchievements'
import { NotificationsList } from './NotificationList'

const Overlay = styled.div`
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  position: fixed;
  z-index: -1;
`

const Container = styled(motion.div)`
  background: white;
  border: 1px solid black;
  border-radius: 0px 0px 8px 8px;
  position: absolute;
  right: -25px;
  top: calc(var(--header-height) / 2);
  width: 400px;

  overflow: hidden;
  z-index: 1000;
  ${media.tabletDown} {
    right: -25px;
  }
  ${media.phoneDown} {
    border: 0;
    border-top: 1px solid var(--black-20);
    border-radius: 0;
    left: 0;
    right: 0;
    width: 100%;
    position: fixed;
    top: 0;
    bottom: 0;
    z-index: 1002;

    display: flex;
    flex-direction: column;
  }
`

const MobileCloseButton = styled.div`
  background: white;
  border-bottom: 1px solid var(--black-20);
  display: flex;
  height: var(--header-height);
  align-items: center;
  justify-content: flex-end;
  padding: 0 12px;
  z-index: 1001;
`

const Header = styled.header`
  border-bottom: 1px solid black;
  display: flex;
  flex-direction: column;
  ${media.phoneDown} {
    border-color: var(--black-20);
  }
`

const HeaderRow = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 16px 24px;
  & + & {
    border-top: 1px solid var(--black-20);
  }
`

const NotificationListContainer = styled(motion.div)`
  max-height: 60vh;
  padding-left: 8px;
  overflow-y: scroll;
  ::-webkit-scrollbar {
    width: 12px;
    background-color: white;
  }
  ::-webkit-scrollbar-track {
    border-radius: 10px;
  }
  ::-webkit-scrollbar-thumb {
    background-color: #ccc;
    background-clip: padding-box;
    border: 4px white solid;

    border-radius: 10px;
  }
  ${media.phoneDown} {
    display: flex;
    flex: 1;
    max-height: 100% !important; // Don't allow motion to animate this value
  }
`

interface Props {
  controls: ReturnType<typeof usePopoverController>
}

export const NotificationCenter = ({ controls }: Props) => {
  const { useFeedStore, feedClient } = useKnockFeed()
  const items = useFeedStore((state) => state.items)
  const unseenItems = items.filter((item) => !item.seen_at)
  const isTabletUp = useMedia(`(min-width: ${phoneMaxWidth + 1}px)`)

  useEffect(() => {
    if (unseenItems.length) {
      feedClient.markAsSeen(unseenItems)
      window.analytics.track('Notifications Marked As Seen', {
        count: unseenItems.length
      })
    }
  }, [unseenItems, feedClient])

  const { user } = useCurrentUser()
  const { data: achivementsData } = useUserAchievements(user?.username)
  const { data: brandsData } = useMyBrands()

  useKeyPressEvent('Escape', () => {
    controls.setOpen(false)
  })

  if (!user) return null

  return (
    <>
      {/* We use this invisible overlay instead of click-away so that you can still
          click the avatar in the header to toggle between open states */}
      <Overlay onClick={() => controls.setOpen(false)} />
      <ReactFocusLock>
        <MaxWidthContainer>
          <AnimatePresence>
            <Container
              aria-modal
              role="dialog"
              initial={{ opacity: 0.5, translateY: isTabletUp ? 0 : -12 }}
              transition={{ opacity: { duration: 0.2 }, translateY: { duration: 0.2 } }}
              animate={{ opacity: 1, translateY: 0 }}
              exit={{ opacity: 0, translateY: 0 }}>
              <ShowForMobile>
                <MobileCloseButton aria-label="Close" onClick={() => controls.setOpen(false)}>
                  <IoCloseSharp size={32} />
                </MobileCloseButton>
              </ShowForMobile>
              <Header>
                <HeaderRow>
                  <UserPreviewLink
                    username={user.username}
                    name={user.fullName || user.username}
                    avatarFullPath={user.avatarFullPath}
                    reviewCount={user.reviewCount}
                    isSupertester={achivementsData?.user?.achievements?.some((a) => a.type === AchievementType.SuperTester)}
                    showPopoverOnHover={false}
                  />

                  <LinkButton href={`/${user.username}`} color="primary" label="View Profile" size="tiny">
                    View Profile
                  </LinkButton>
                </HeaderRow>

                {brandsData?.myContributableBrands.map((brand) => (
                  <HeaderRow key={`brand-${brand.id}`}>
                    <BrandPreviewLink slug={brand.slug} />

                    <LinkButton href={`/brandmin/${brand.slug}`} color="secondary" label="View Profile" size="tiny">
                      Manage brand
                    </LinkButton>
                  </HeaderRow>
                ))}
              </Header>

              <NotificationListContainer
                {...(isTabletUp && {
                  initial: { maxHeight: '20vh' },
                  transition: { maxHeight: { duration: 0.2 } },
                  animate: { maxHeight: '60vh' },
                  exit: { maxHeight: '20vh' }
                })}>
                <NotificationsList onClickItem={() => controls.setOpen(false)} />
              </NotificationListContainer>
            </Container>
          </AnimatePresence>
        </MaxWidthContainer>
      </ReactFocusLock>
    </>
  )
}
